import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  SimpleChanges,
  Input,
} from "@angular/core";
import { faEdit, faHistory } from "@fortawesome/free-solid-svg-icons";
import { EmbargoManagementPageDataService } from "../embargo-management-page-data.service";
import { NzMessageService } from "ng-zorro-antd/message";
import dayjs from "dayjs";
import { VAIconRegistryService, close } from "@va/core-library/icons";

interface GridData {
  id: number;
  broker: string;
  broker_id: number;
  buyside: string;
  buyside_id: number;
  embarogo_id: number;
  model_data: number;
  model_download: number;
  research_download: number;
}

interface HistoryGridData {
  Action: string;
  BrokerName: string;
  Broker_Id: number;
  BuysideName: string;
  Buyside_Id: number;
  Embargo_Type: string;
  NewValue: number;
  PreviousValue: number;
  Updated_At: string;
  UserEmail: string;
  FirstName: string;
  LastName: string;
  UserId: number;
}

enum EmbargoAction {
  Inserted = "Add",
  Updated = "Edit",
  Deleted = "Delete",
}

@Component({
  selector: "app-embargo-management-grid",
  templateUrl: "./embargo-management-grid.component.html",
  styleUrls: ["./embargo-management-grid.component.scss"],
})
export class EmbargoManagementGridComponent implements OnInit {
  loading = false;
  checked = false;
  indeterminate = false;
  pageIndex = 1;
  widthConfig = [
    "50px", //Select
    "250px", //Broker
    "300px", //Buyside
    "150px", //Model Data Usage Embargo
    "150px", //Model Download Embargo
    "150px", //Research Download Embargo
  ];
  gridSize = { y: "", x: "auto" };
  pageLimit = 25;
  buysideKeyword = "";
  brokerKeyword = "";
  sortBy = "";
  sortOrder = "";
  total = 0;
  totalHistoryData = 0;
  faHistory = faHistory;
  faEdit = faEdit;
  gridData: GridData[] = [];
  mapOfSort = {
    broker: "ascend",
    buyside: null,
  };
  sortCol = ["broker", "buyside"];
  hoverIndex = null;
  setOfCheckeddata = new Set<object>();
  count = 0;
  isEmbargoHistoryVisible = false;
  embargoHistoryGridColumnList = [
    {
      header: "Date/Time",
    },
    {
      header: "User",
    },
    {
      header: "Data Type",
    },
    {
      header: "Action",
    },
    {
      header: "Previous Value",
    },
    {
      header: "New Value",
    },
  ];
  embargoHistoryGridColumnFields = [
    {
      field: "Updated_At",
      tooltip: true,
    },
    {
      field: "User",
      tooltip: true,
      class: "row-data-width-250",
    },
    {
      field: "Embargo_Type",
      tooltip: true,
    },
    {
      field: "Action",
      tooltip: false,
    },
    {
      field: "PreviousValue",
      tooltip: false,
      class: "text-align-center",
    },
    {
      field: "NewValue",
      tooltip: false,
      class: "text-align-center",
    },
  ];
  embargoHistoryGridData: any[] = [];
  embargoHistoryTitle = "Embargo Override";
  embargoHistoryWidthConfig = [
    "150px",
    "280px",
    "140px",
    "50px",
    "120px",
    "100px",
  ];

  historyDetails = null;

  @Input() fetchGrid;
  @Input() isFilterEnable;
  @Input() reloadTrigger;
  @Output() setDefaultEmbargo = new EventEmitter();
  @Output() editEmbargoOverrideDetails = new EventEmitter();
  @Output() changeKeyword = new EventEmitter();
  @Output() changeSortObj = new EventEmitter();
  @Output() countChanged = new EventEmitter();
  @Output() deleteCount = new EventEmitter();

  constructor(
    private dataService: EmbargoManagementPageDataService,
    private message: NzMessageService,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([close]);
  }

  ngOnInit() {
    this.loading = true;
    this.getGridData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.fetchGrid &&
      typeof changes.fetchGrid.previousValue !== "undefined" &&
      changes.fetchGrid.currentValue !== changes.fetchGrid.previousValue
    ) {
      this.fetchGridData();
    }

    if (
      changes.reloadTrigger &&
      typeof changes.reloadTrigger.previousValue !== "undefined" &&
      changes.reloadTrigger.currentValue !== changes.reloadTrigger.previousValue
    ) {
      this.resetFilter();
    }
  }

  resetFilter() {
    this.resetSort();
    this.reset("");
  }

  resetSort() {
    this.sortBy = "";
    this.sortOrder = "";
    this.mapOfSort = {
      broker: "ascend",
      buyside: null,
    };
    this.changeSortObj.emit({
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
    });
  }

  fetchGridData(): void {
    this.loading = true;
    this.getGridData();
  }

  getGridData() {
    this.dataService
      .getData(
        this.pageIndex - 1,
        this.pageLimit,
        this.buysideKeyword,
        this.brokerKeyword,
        this.sortBy,
        this.sortOrder,
      )
      .subscribe(
        (data) => {
          this.gridData = data["embargo_data"].map((data: GridData) => {
            return {
              broker: data["broker"],
              brokerId: data["broker_id"],
              buyside: data["buyside"],
              buysideId: data["buyside_id"],
              modelData: data["model_data"],
              modelDownload: data["model_download"],
              researchDownload: data["research_download"],
            };
          });

          this.pageLimit = data["meta"]["pagelimit"];
          this.total = data["meta"]["total"];
          this.pageIndex = Number(data["meta"]["pageoffset"]) + 1;
          this.count = 0;

          this.setDefaultEmbargo.emit(data["meta"]["defaults"]);
          this.deleteCount.emit(this.count);
          this.loading = false;
        },
        (err) => {
          console.error(err);
          this.loading = false;
        },
      );
  }

  search(): void {
    this.pageIndex = 1;
    this.fetchGridData();
    this.changeKeyword.emit({
      buyside: this.buysideKeyword,
      broker: this.brokerKeyword,
    });
  }

  reset(col): void {
    if (col === "broker") {
      this.brokerKeyword = "";
    } else if (col === "buyside") {
      this.buysideKeyword = "";
    } else {
      this.brokerKeyword = "";
      this.buysideKeyword = "";
    }

    this.fetchGridData();
    this.changeKeyword.emit({
      buyside: this.buysideKeyword,
      broker: this.brokerKeyword,
    });
  }

  sort(col, $event): void {
    this.sortBy = col;
    this.sortOrder = $event === "ascend" ? "asc" : "desc";
    for (const key in this.mapOfSort) {
      this.mapOfSort[key] = key === col ? $event : null;
    }
    this.changeSortObj.emit({
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
    });
    this.fetchGridData();
  }

  onPaginationChange() {
    this.fetchGridData();
  }

  editEmbargoOverride(data) {
    this.editEmbargoOverrideDetails.emit(data);
  }

  deleteCheckboxClicked(checked: boolean, row): void {
    if (checked) {
      this.setOfCheckeddata.add(row);
      this.count = this.count + 1;
    } else {
      this.setOfCheckeddata.delete(row);
      this.count = this.count - 1;
    }
    this.countChanged.emit(this.setOfCheckeddata);
    this.deleteCount.emit(this.count);
  }

  fetchEmbargoHistory = (brokerId, buysideId, pageIndex, pageLimit) => {
    return this.dataService.getEmbargoHistory(
      brokerId,
      buysideId,
      pageIndex,
      pageLimit,
    );
  };

  onPageDetailsChange(index, pageSize) {
    if (this.historyDetails) {
      this.showEmbargoHistory(this.historyDetails, index, pageSize, false);
    }
  }

  showEmbargoHistory = (
    data,
    pageIndex = 1,
    pageSize = 10,
    updateData = true,
  ) => {
    if (updateData) this.historyDetails = data;
    this.dataService
      .getEmbargoHistory(data.brokerId, data.buysideId, pageIndex - 1, pageSize)
      .subscribe(
        (details: HistoryGridData[]) => {
          this.totalHistoryData = details["total"];
          this.embargoHistoryGridData = details["data"].map((data) => {
            return {
              Updated_At: dayjs(data.Updated_At).format("MM/DD/YYYY HH:mm"),
              User: `${data.UserEmail} (${data.FirstName} ${data.LastName})`,
              Embargo_Type: data.Embargo_Type,
              Action: `${EmbargoAction[data.Action]}`,
              PreviousValue:
                data.PreviousValue !== null ? data.PreviousValue : "-",
              NewValue: data.NewValue !== null ? data.NewValue : "-",
            };
          });
          this.isEmbargoHistoryVisible = true;
        },
        (err) => {
          console.error(err);
          this.message.create("error", `${err["error"]["errors"]}`);
        },
      );
  };

  closeModalEmbargoHistory = (status) => {
    this.isEmbargoHistoryVisible = status;
  };
}
